import React, { useState } from 'react';
import { Power } from 'lucide-react';

interface PowerButtonProps {
  onPowerOn: () => void;
}

export const PowerButton: React.FC<PowerButtonProps> = ({ onPowerOn }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const handlePowerClick = () => {
    if (isStarting) return;
    
    setIsPressed(true);
    setIsStarting(true);
    
    // Effet sonore de clic (optionnel)
    try {
      const clickSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
      clickSound.volume = 0.3;
      clickSound.play().catch(() => {});
    } catch (e) {}
    
    // Animation de démarrage
    setTimeout(() => {
      onPowerOn();
    }, 800);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
      {/* Fond avec effet CRT subtil */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-black to-gray-900"></div>
      
      {/* Scanlines CRT */}
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{
             backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,255,0.03) 2px, rgba(0,255,255,0.03) 4px)',
           }}>
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 text-center">
        {/* Bouton Power */}
        <div className="relative">
          {/* Base du bouton (partie enfoncée dans le boîtier) */}
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-b from-slate-800 to-slate-900 shadow-inner"
               style={{
                 boxShadow: 'inset 0 4px 12px rgba(0, 0, 0, 0.8), inset 0 -2px 6px rgba(255, 255, 255, 0.1)'
               }}>
            
            {/* Bouton principal avec effet 3D */}
            <button
              onClick={handlePowerClick}
              disabled={isStarting}
              className={`
                absolute top-2 left-2 w-28 h-28 rounded-full transition-all duration-150 transform
                ${isPressed || isStarting 
                  ? 'translate-y-1 shadow-sm' 
                  : 'translate-y-0 shadow-lg hover:translate-y-0.5'
                }
                disabled:cursor-not-allowed
              `}
              style={{
                background: isPressed || isStarting 
                  ? 'linear-gradient(145deg, #1e293b, #334155)' 
                  : 'linear-gradient(145deg, #475569, #334155)',
                boxShadow: isPressed || isStarting
                  ? `
                    inset 0 2px 8px rgba(0, 0, 0, 0.6),
                    inset 0 -1px 4px rgba(255, 255, 255, 0.1),
                    0 2px 4px rgba(0, 0, 0, 0.3)
                  `
                  : `
                    0 6px 12px rgba(0, 0, 0, 0.4),
                    0 2px 4px rgba(0, 0, 0, 0.2),
                    inset 0 1px 2px rgba(255, 255, 255, 0.2),
                    inset 0 -1px 2px rgba(0, 0, 0, 0.2)
                  `
              }}
            >
              {/* Surface du bouton avec reflet */}
              <div className="relative w-full h-full rounded-full overflow-hidden">
                {/* Reflet en haut */}
                <div className="absolute top-2 left-4 right-4 h-6 rounded-full opacity-30"
                     style={{
                       background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.4), transparent)'
                     }}>
                </div>
                
                {/* Icône Power */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Power 
                    className={`w-12 h-12 transition-all duration-200 ${
                      isPressed || isStarting 
                        ? 'text-cyan-300 drop-shadow-lg' 
                        : 'text-slate-300'
                    }`}
                    style={{
                      filter: isPressed || isStarting 
                        ? 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.8))' 
                        : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))'
                    }}
                  />
                </div>
                
                {/* LED indicateur intégré */}
                <div className={`
                  absolute bottom-3 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full transition-all duration-300
                  ${isStarting 
                    ? 'bg-cyan-400 animate-pulse' 
                    : 'bg-slate-600'
                  }
                `}
                     style={{
                       boxShadow: isStarting 
                         ? '0 0 8px rgba(34, 211, 238, 0.8), inset 0 1px 1px rgba(255, 255, 255, 0.3)' 
                         : 'inset 0 1px 1px rgba(0, 0, 0, 0.5)'
                     }}>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Effet de grille en arrière-plan */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
           style={{
             backgroundImage: `
               linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px),
               linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)
             `,
             backgroundSize: '50px 50px'
           }}>
      </div>
    </div>
  );
};